import config from 'src/config';
import profile from 'src/store/profile';

// Enable logging when running locally (development) only.
// If `config.apiLog` is explicitly set (true/false), respect it but still
// suppress logs in production builds (NODE_ENV === 'production').
const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
const shouldLog = (typeof config.apiLog === 'boolean') ? config.apiLog : isDev;

const maskHeaders = (headers = {}) => {
  const h = { ...headers };
  if (h.Authorization) {
    h.Authorization = String(h.Authorization).replace(/(Bearer\s+).*/i, '$1***');
  }
  return h;
};

const isAbsolute = (u) => /^https?:\/\//i.test(u);

const buildUrl = (path) => {
  if (!path) return path;
  if (isAbsolute(path)) return path;
  const base = config.server || '';
  return `${base}${String(path).replace(/^\/+/, '')}`;
};

async function request(path, options = {}) {
  const { method = 'GET', body, headers = {}, rawBody = false, ...rest } = options;
  let url = buildUrl(path);

  // Автоматичний cache-buster для всіх GET-запитів
  if (method.toUpperCase() === 'GET') {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}_t=${Date.now()}`;
  }

  const reqHeaders = {
    Accept: 'application/json',
    ...headers,
  };

  if (profile && profile.token) {
    reqHeaders.Authorization = `Bearer ${profile.token}`;
  }

  let reqBody = body;
  if (!rawBody && body !== undefined && body !== null && typeof body === 'object' && !(body instanceof FormData)) {
    reqHeaders['Content-Type'] = 'application/json; charset=utf-8';
    reqBody = JSON.stringify(body);
  }

  if (shouldLog) {
    console.log('[api] request', {
      method,
      url,
      headers: maskHeaders(reqHeaders),
      body: body instanceof FormData ? '[FormData]' : body,
      rawBody,
    });
  }

  const res = await fetch(url, { method, headers: reqHeaders, body: reqBody, ...rest });
  const resClone = res.clone();
  const contentType = res.headers.get ? (res.headers.get('content-type') || '') : '';

  if (!res.ok) {
    let data;
    try {
      data = contentType.includes('application/json') ? await resClone.json() : await resClone.text();
    } catch (e) {
      data = await resClone.text();
    }
    if (shouldLog) {
      console.log('[api] error', {
        method,
        url,
        status: res.status,
        data,
      });
    }
    const err = new Error(res.statusText || 'HTTP Error');
    err.status = res.status;
    err.data = data;
    throw err;
  }

  if (contentType.includes('application/json')) {
    const data = await res.json();
    if (shouldLog) {
      console.log('[api] response', { method, url, status: res.status, data });
    }
    return data;
  }
  const text = await res.text();
  if (shouldLog) {
    console.log('[api] response', { method, url, status: res.status, data: text });
  }
  return text;
}

function logError(context, err) {
  if (!shouldLog) return;
  try {
    const info = {
      context,
      message: err && err.message ? err.message : String(err),
      stack: err && err.stack ? err.stack : undefined,
      status: err && err.status ? err.status : undefined,
      data: err && err.data ? err.data : undefined,
    };
    console.error('[api] error', info);
  } catch (e) {
    console.error('[api] error', context, err);
  }
}

const api = {
  request,
  get: (path, opts) => request(path, { method: 'GET', ...opts }),
  post: (path, body, opts = {}) => request(path, { method: 'POST', body, ...opts }),
  put: (path, body, opts = {}) => request(path, { method: 'PUT', body, ...opts }),
  patch: (path, body, opts = {}) => request(path, { method: 'PATCH', body, ...opts }),
  delete: (path, opts = {}) => request(path, { method: 'DELETE', ...opts }),
  logError: (context, err) => logError(context, err),
};

export default api;